<template>
  <div :class="$style.inBetweenTimeSwitch">
    <div :class="$style.switchContainer">
      <div :class="$style.labelContainer">
        <div :class="$style.primaryLabel">
          {{ props.isEnabled ? "End your workday" : "Start your workday" }}
        </div>
        <div :class="$style.secondaryLabel">
          {{
            props.isEnabled
              ? "Stop tracking in-between time"
              : "Begin tracking in-between time"
          }}
        </div>
      </div>
      <div :class="$style.switchWrapper">
        <SwitchRoot
          :model-value="props.isEnabled"
          @update:modelValue="handleToggle"
          :class="$style.switchRoot"
        >
          <SwitchThumb :class="$style.switchThumb" />
        </SwitchRoot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { SwitchRoot, SwitchThumb } from "reka-ui";

interface Props {
  isEnabled: boolean;
}

interface Emits {
  (e: "toggle", enabled: boolean): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

function handleToggle(enabled: boolean) {
  emit("toggle", enabled);
}
</script>

<style module>
.inBetweenTimeSwitch {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  backdrop-filter: blur(10px);
}

.switchContainer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.labelContainer {
  flex: 1;
}

.primaryLabel {
  font-size: 18px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 4px;
}

.secondaryLabel {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
}

.switchWrapper {
  flex-shrink: 0;
}

.switchRoot {
  width: 50px;
  height: 28px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 9999px;
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s ease;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}

.switchRoot[data-state="checked"] {
  background-color: var(--secondary-color);
}

.switchThumb {
  display: block;
  width: 24px;
  height: 24px;
  background-color: white;
  border-radius: 9999px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
  transform: translateX(2px);
  will-change: transform;
}

.switchThumb[data-state="checked"] {
  transform: translateX(24px);
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .switchContainer {
    align-items: flex-start;
    gap: 16px;
  }

  .switchWrapper {
    align-self: flex-end;
  }

  .primaryLabel {
    font-size: 16px;
  }

  .secondaryLabel {
    font-size: 13px;
  }
}
</style>
